import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { SettingsSubPageWrapper } from '../Settings';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Percent, Plus, Trash2, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BenchmarkMarginsSettings() {
  const [margins, setMargins] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => { fetchMargins(); }, []);

  const fetchMargins = async () => {
    try {
      const res = await axios.get(`${API}/settings/benchmark-margins`, { withCredentials: true });
      setMargins(res.data);
    } catch { toast.error('Failed to load margins'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/benchmark-margins`, margins, { withCredentials: true });
      toast.success('Benchmark margins saved');
    } catch { toast.error('Failed to save margins'); }
    finally { setSaving(false); }
  };

  const handleAddCategory = () => {
    const key = newCategory.trim().toLowerCase();
    if (!key) return;
    if (margins[key] !== undefined) {
      toast.error('Category already exists');
      return;
    }
    setMargins(prev => ({ ...prev, [key]: 30 }));
    setNewCategory('');
  };

  const handleRemoveCategory = (key) => {
    if (key === 'restaurant' || key === 'retail') {
      toast.error('Cannot remove default categories');
      return;
    }
    setMargins(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (loading) {
    return (
      <SettingsSubPageWrapper title="Benchmark Margins">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SettingsSubPageWrapper>
    );
  }

  return (
    <SettingsSubPageWrapper title="Benchmark Margins">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Benchmark Margins
        </h1>
        <p className="text-muted-foreground mt-1">
          Set target cost percentages for different recipe categories. Recipes exceeding these thresholds will be flagged as cost alerts.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5" />Target Cost Percentages
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(margins).map(([key, value]) => (
            <div key={key} className="flex items-center gap-4" data-testid={`margin-row-${key}`}>
              <div className="w-48">
                <Label className="text-sm font-medium capitalize">{key}</Label>
              </div>
              <div className="flex items-center gap-2 flex-1 max-w-xs">
                <Input
                  type="number" step="0.1" min="0" max="100"
                  value={value}
                  onChange={(e) => setMargins(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                  data-testid={`margin-input-${key}`}
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              {key !== 'restaurant' && key !== 'retail' && (
                <Button variant="ghost" size="icon" onClick={() => handleRemoveCategory(key)}
                  data-testid={`remove-margin-${key}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}

          <div className="flex items-center gap-4 pt-4 border-t">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="New category name..."
              className="w-48"
              data-testid="new-margin-category-input"
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
            />
            <Button variant="outline" onClick={handleAddCategory} data-testid="add-margin-category-btn">
              <Plus className="w-4 h-4 mr-2" />Add Category
            </Button>
          </div>

          <div className="pt-4">
            <Button onClick={handleSave} disabled={saving} data-testid="save-margins-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Margins'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="text-sm text-muted-foreground space-y-2">
            <p><strong>How it works:</strong> When a recipe is created or edited, its category determines which benchmark margin applies.</p>
            <p>If the recipe's actual cost percentage exceeds the benchmark for its category, it will be flagged as a cost alert.</p>
            <p>For example, a "Restaurant" recipe with a 30% benchmark means the ingredient cost should not exceed 30% of the selling price (excl. VAT).</p>
          </div>
        </CardContent>
      </Card>
    </SettingsSubPageWrapper>
  );
}
